import { useEffect, useMemo, useRef } from 'react';
import {
    operatorObjectiveWords,
    quoteObjectiveIdentity,
    resolveQuoteObjective,
    storeQuoteObjective,
    type QuoteObjectiveInputs,
} from '../services/quote-objective';

/**
 * The one objective of the open quote, as App hands it to Stage 4 and Stage 5.
 *
 * `text` is what both stages show, print, email and save. `hasQuote` tells
 * Stage 4 that Stage 5 has a quote to show it on, so Stage 4 shows the field
 * whenever Stage 5 does.
 */
export interface QuoteObjectiveValue {
    text: string;
    hasQuote: boolean;
}

/**
 * Computed ONCE per App render from the stored values (Peter, 2026-09-29:
 * "Stage 4 and Stage 5 Scope Objectives should ALWAYS match"). App calls this
 * exactly once and passes the result to AppStageViewsContainer (Stage 4) and
 * AppQuoteEditorModalWrapper (Stage 5). A state update that changes formData,
 * the quote or the scope changes the value for both stages in the same render.
 *
 * `stage5Scope` is the scope Stage 5 publishes once the operator edits in it
 * (FullQuoteEditorModal onObjectiveScopeChange), and only while it is open.
 * A Stage 5 item edit changes the editor's own scope, not App's, and Back and
 * Save then take that scope into Stage 4. So while it is set, the objective
 * is derived from it: Stage 5 shows and prints the objective of the items it
 * shows, and Stage 4 shows the same sentence after Back.
 *
 * `projectSummary` is App's Stage 1.5 summary. Before any quote, its
 * objective is the one both stages show, and the one the Stage 5 scope draft
 * carries (buildStage5DraftQuoteFromScope reads the same summary).
 */
export function useQuoteObjective({
    formData,
    quoteData,
    scope,
    stage5Scope = null,
    projectSummary = null,
}: {
    formData?: QuoteObjectiveInputs['formData'];
    quoteData?: QuoteObjectiveInputs['quoteData'];
    scope?: any[] | null;
    stage5Scope?: any[] | null;
    projectSummary?: QuoteObjectiveInputs['projectSummary'];
}): QuoteObjectiveValue {
    const objectiveScope = Array.isArray(stage5Scope) ? stage5Scope : scope;
    const formObjective = formData?.scopeObjective;
    const formEditedByUser = formData?.scopeObjectiveEditedByUser === true;
    const quoteOwnKept = formData?.scopeObjectiveQuoteOwn;
    const quoteOwnKeptOf = formData?.scopeObjectiveQuoteOwnOf;
    const quoteObjective = quoteData?.scopeObjective;
    const generatedBy = quoteData?.meta?.generatedBy;
    const generatedAt = quoteData?.meta?.generatedAt;
    const hasQuote = Boolean(quoteData && typeof quoteData === 'object');
    const summaryObjective = projectSummary?.scope_objective || projectSummary?.scopeObjective;
    return useMemo(() => ({
        text: resolveQuoteObjective({
            formData: {
                scopeObjective: formObjective,
                scopeObjectiveEditedByUser: formEditedByUser,
                scopeObjectiveQuoteOwn: quoteOwnKept,
                scopeObjectiveQuoteOwnOf: quoteOwnKeptOf,
            },
            quoteData: hasQuote ? { scopeObjective: quoteObjective, meta: { generatedBy, generatedAt } } : null,
            scope: Array.isArray(objectiveScope) ? objectiveScope : [],
            projectSummary: { scope_objective: summaryObjective },
        }),
        hasQuote,
    }), [formObjective, formEditedByUser, quoteOwnKept, quoteOwnKeptOf, quoteObjective, generatedBy, generatedAt, hasQuote, objectiveScope, summaryObjective]);
}

/**
 * Keeps App's quote saying the objective both stages show (storeQuoteObjective),
 * so the saved quote, the copy kept when it is sent, the revision diff, the
 * Run Sheet and the Assistant read the same objective as Stage 4, Stage 5 and
 * the PDF. App calls this once, after useQuoteObjective.
 *
 * Every objective change is stored on the form (quoteObjectiveEdit) by many
 * paths: typing in either stage, the Assistant, Undo and Redo in either stage,
 * a regeneration merge. This follows the form, so each of them reaches the
 * quote in the next render, and a quote that lands later (a generation) is
 * brought in line too.
 *
 * Opening a quote is not an edit. A change of `loadKey` (the project and the
 * load that painted it) shows what was saved and writes nothing.
 */
export function useQuoteObjectiveStorage({
    formData,
    setFormData,
    quoteData,
    setQuoteData,
    loadKey,
}: {
    formData: any;
    setFormData: (update: (prev: any) => any) => void;
    quoteData: any;
    setQuoteData: (update: (prev: any) => any) => void;
    loadKey: string;
}): void {
    const words = operatorObjectiveWords(formData);
    const quoteObjective = quoteData?.scopeObjective;
    const quoteIdentity = quoteData && typeof quoteData === 'object' ? quoteObjectiveIdentity(quoteData) : null;
    const lastRef = useRef<{ loadKey: string; words: string | null } | null>(null);
    useEffect(() => {
        const last = lastRef.current;
        lastRef.current = { loadKey, words };
        if (!last || last.loadKey !== loadKey) return;
        const previousWords = last.words;
        const target = storeQuoteObjective({ formData, quoteData, previousWords });
        // Each writer re-reads its own latest value. The quote's own objective
        // is read from the quote as it was before the words were written in.
        if (target.quoteData !== quoteData) {
            setQuoteData(prev => storeQuoteObjective({ formData, quoteData: prev, previousWords }).quoteData);
        }
        if (target.formData !== formData) {
            setFormData(prev => storeQuoteObjective({ formData: prev, quoteData, previousWords }).formData);
        }
        // Runs on the objective fields only: the form and the quote change for
        // many other reasons, and none of those is an objective change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadKey, words, quoteObjective, quoteIdentity]);
}
